import { useQueries } from '@tanstack/react-query';
import { typedFetch } from '@/lib/api-client';

// REQ-ACTIVATE-001: Activation State Detection

export interface ActivationState {
  hasApiKey: boolean;
  hasProviderKey: boolean;
  hasFirstRequest: boolean;
  setupCompleted: boolean;
  isLoading: boolean;
  hasError: boolean;
  errorMessage?: string;
}

interface ApiKey {
  id: string;
  name: string;
}

interface ProviderKey {
  id: string;
  provider: string;
}

interface AnalyticsSummary {
  totalRequests: number | null;
}

/**
 * REQ-ACTIVATE-001: Detects user activation state
 * Fetches data from 3 endpoints in parallel:
 * - GET /api/v1/keys (API keys)
 * - GET /api/v1/provider-keys (Provider keys)
 * - GET /api/v1/analytics/summary (Analytics - all-time query)
 *
 * Returns activation state indicating setup completion.
 * Caches for 30 seconds (stale time).
 * Handles errors gracefully (returns false for failed checks).
 */
export function useActivationState(): ActivationState {
  const results = useQueries({
    queries: [
      {
        queryKey: ['apiKeys'],
        queryFn: async () => {
          // REQ-ACTIVATE-001
          return await typedFetch<ApiKey[]>('/api/v1/keys');
        },
        staleTime: 30 * 1000, // 30 seconds
        retry: false, // Don't retry on 401 - user needs to fix auth
      },
      {
        queryKey: ['providerKeys'],
        queryFn: async () => {
          // REQ-ACTIVATE-001
          return await typedFetch<ProviderKey[]>('/api/v1/provider-keys');
        },
        staleTime: 30 * 1000, // 30 seconds
        retry: false, // Don't retry on 401 - user needs to fix auth
      },
      {
        queryKey: ['analyticsSummary'],
        queryFn: async () => {
          // REQ-ACTIVATE-001
          return await typedFetch<AnalyticsSummary>('/api/v1/analytics/summary');
        },
        staleTime: 30 * 1000, // 30 seconds
        retry: false, // Don't retry on 401 - user needs to fix auth
      },
    ],
  });

  const [apiKeysQuery, providerKeysQuery, analyticsSummaryQuery] = results;

  // REQ-ACTIVATE-001: Returns loading state while fetching
  const isLoading =
    apiKeysQuery.isLoading || providerKeysQuery.isLoading || analyticsSummaryQuery.isLoading;

  // REQ-ACTIVATE-004: Global error only when ALL endpoints fail (not just one)
  const hasError =
    apiKeysQuery.isError && providerKeysQuery.isError && analyticsSummaryQuery.isError;

  const errorMessage = hasError
    ? 'Unable to verify setup status. You can still navigate to setup pages.'
    : undefined;

  // REQ-ACTIVATE-003: Graceful degradation - fallback to false if endpoint fails
  // REQ-ACTIVATE-001: Check if ≥1 API key exists
  const hasApiKey = apiKeysQuery.isError
    ? false
    : Array.isArray(apiKeysQuery.data) && apiKeysQuery.data.length > 0;

  // REQ-ACTIVATE-003: Graceful degradation - fallback to false if endpoint fails
  // REQ-ACTIVATE-001: Check if ≥1 provider key exists
  const hasProviderKey = providerKeysQuery.isError
    ? false
    : Array.isArray(providerKeysQuery.data) && providerKeysQuery.data.length > 0;

  // REQ-ACTIVATE-003: Graceful degradation - fallback to false if endpoint fails
  // REQ-ACTIVATE-001: Check if ≥1 API request exists
  const hasFirstRequest = analyticsSummaryQuery.isError
    ? false
    : analyticsSummaryQuery.data != null &&
      typeof analyticsSummaryQuery.data.totalRequests === 'number' &&
      analyticsSummaryQuery.data.totalRequests > 0;

  // REQ-ACTIVATE-001: All three checks must pass
  const setupCompleted = hasApiKey && hasProviderKey && hasFirstRequest;

  return {
    hasApiKey,
    hasProviderKey,
    hasFirstRequest,
    setupCompleted,
    isLoading,
    hasError,
    errorMessage,
  };
}
